import { apiGet, apiPost, apiPut, apiDelete } from '~/utils/api'

/**
 * User address response (matches backend UserAddressResponse)
 */
export interface UserAddress {
  id: string
  label: string | null
  fullName: string
  phone: string | null
  streetLine1: string
  streetLine2: string | null
  city: string
  state: string | null
  postalCode: string
  countryCode: string
  isDefault: boolean
}

/**
 * Create address request (matches backend CreateUserAddressRequest)
 */
export interface CreateAddressRequest {
  label?: string
  fullName: string
  phone?: string
  streetLine1: string
  streetLine2?: string
  city: string
  state?: string
  postalCode: string
  countryCode: string
  isDefault?: boolean
}

/**
 * Address API composable
 */
export function useAddressAPI() {
  const loading = ref(false)
  const error = ref<string | null>(null)

  /**
   * Get user's saved addresses
   */
  async function fetchAddresses(): Promise<UserAddress[]> {
    loading.value = true
    error.value = null

    try {
      return await apiGet<UserAddress[]>('/api/app/v1/user/addresses')
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error'
      throw e
    } finally {
      loading.value = false
    }
  }

  /**
   * Create a new address
   */
  async function createAddress(request: CreateAddressRequest): Promise<UserAddress> {
    loading.value = true
    error.value = null

    try {
      return await apiPost<UserAddress>('/api/app/v1/user/addresses', request)
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error'
      throw e
    } finally {
      loading.value = false
    }
  }

  /**
   * Update an existing address
   */
  async function updateAddress(id: string, request: CreateAddressRequest): Promise<UserAddress> {
    loading.value = true
    error.value = null

    try {
      return await apiPut<UserAddress>(`/api/app/v1/user/addresses/${id}`, request)
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error'
      throw e
    } finally {
      loading.value = false
    }
  }

  /**
   * Delete an address
   */
  async function deleteAddress(id: string): Promise<void> {
    loading.value = true
    error.value = null

    try {
      await apiDelete(`/api/app/v1/user/addresses/${id}`)
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error'
      throw e
    } finally {
      loading.value = false
    }
  }

  /**
   * Set an address as default
   */
  async function setDefaultAddress(id: string): Promise<UserAddress> {
    loading.value = true
    error.value = null

    try {
      return await apiPost<UserAddress>(`/api/app/v1/user/addresses/${id}/default`)
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error'
      throw e
    } finally {
      loading.value = false
    }
  }

  return {
    loading,
    error,
    fetchAddresses,
    createAddress,
    updateAddress,
    deleteAddress,
    setDefaultAddress,
  }
}
